import { APP_NAME } from '../constants'
import { composeStartParam } from '../functions/startParam'
import { isInTelegram } from '../hooks/useTelegram'

/**
 * Get BOT_USERNAME from environment, throws if not defined
 * @throws {ShareConfigError} If BOT_USERNAME is not defined
 */
const getBotUsername = (): string => {
  const botUsername = process.env['BOT_USERNAME']
  if (!botUsername) {
    throw new Error('BOT_USERNAME is not defined in environment variables')
  }
  return botUsername
}

/**
 * Get APP_URL from environment, throws if not defined
 * @throws {Error} If APP_URL is not defined
 */
const getAppUrl = (): string => {
  const appUrl = process.env['APP_URL']
  if (!appUrl) {
    throw new Error('APP_URL is not defined in environment variables')
  }
  // Remove trailing slash if present
  return appUrl.replace(/\/$/, '')
}

/**
 * Generate a Telegram deep link referral URL
 * Format: https://t.me/{BOT_USERNAME}/{APP_NAME}?startapp=ref_{referralKey}
 */
export const generateTelegramReferralUrl = (referralKey: string): string => {
  const botUsername = getBotUsername()
  const startParam = composeStartParam(referralKey, null)
  return `https://t.me/${botUsername}/${APP_NAME}?startapp=${startParam}`
}

/**
 * Generate a browser-friendly referral URL
 * Format: {APP_URL}?zyn={referralKey}
 */
export const generateBrowserReferralUrl = (referralKey: string): string => {
  const appUrl = getAppUrl()
  return `${appUrl}?zyn=${referralKey}`
}

/**
 * Generate a referral URL for sharing
 * Auto-detects context: Telegram deep link in Telegram, browser URL otherwise
 *
 * @param referralKey - Referral key (user's referral identifier)
 * @returns Referral URL
 *
 * @example
 * // In Telegram:
 * generateReferralUrl('548357ea')
 * // Returns: 'https://t.me/VoteBoxBot/votebox?startapp=zyn_548357ea'
 *
 * // In browser:
 * generateReferralUrl('548357ea')
 * // Returns: 'https://tma.votebox.io?zyn=548357ea'
 */
export const generateReferralUrl = (referralKey: string): string => {
  if (isInTelegram()) {
    return generateTelegramReferralUrl(referralKey)
  }
  return generateBrowserReferralUrl(referralKey)
}

/**
 * Generate a Telegram box deep link
 * Format: https://t.me/{BOT_USERNAME}/{APP_NAME}?startapp={startParam}
 */
export const generateTelegramBoxUrl = (
  boxId: number,
  referralKey?: string | null,
): string => {
  const botUsername = getBotUsername()
  const startParam = composeStartParam(referralKey || null, boxId)
  return `https://t.me/${botUsername}/${APP_NAME}?startapp=${startParam}`
}

/**
 * Generate a browser-friendly box URL
 * Format: {APP_URL}/box/{boxId}?zyn={referralKey}
 */
export const generateBrowserBoxUrl = (
  boxId: number,
  referralKey?: string | null,
): string => {
  const appUrl = getAppUrl()
  const baseUrl = `${appUrl}/box/${boxId}`
  if (referralKey) {
    return `${baseUrl}?zyn=${referralKey}`
  }
  return baseUrl
}

/**
 * Generate a box deep link for sharing
 * Auto-detects context: Telegram deep link in Telegram, browser URL otherwise
 *
 * @param boxId - Box ID to share
 * @param referralKey - Optional referral key to track the share
 * @returns Box URL
 *
 * @example
 * // In Telegram:
 * generateBoxDeepLinkUrl(123, 'abc-def-ghi')
 * // Returns: 'https://t.me/VoteBoxBot/votebox?startapp=zyn_abc-def-ghi_box_123'
 *
 * // In browser:
 * generateBoxDeepLinkUrl(123, 'abc-def-ghi')
 * // Returns: 'https://tma.votebox.io/box/123?zyn=abc-def-ghi'
 */
export const generateBoxDeepLinkUrl = (
  boxId: number,
  referralKey?: string | null,
): string => {
  if (isInTelegram()) {
    return generateTelegramBoxUrl(boxId, referralKey)
  }
  return generateBrowserBoxUrl(boxId, referralKey)
}

